import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { getExplorerAddressUrl } from '../../services/api';
import { Icons } from './DashboardLayout';

export default function SettingsPage() {
  const { user, agents } = useAuth();

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Profile Section */}
      <SettingsCard title="Profile" icon={<Icons.Settings size={18} />}>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center text-white text-xl font-medium ring-2 ring-white/10">
            {user?.walletAddress?.slice(2, 4).toUpperCase() || 'U'}
          </div>
          <div>
            <p className="text-white font-medium text-lg">Developer</p>
            {user?.walletAddress && (
              <a 
                href={getExplorerAddressUrl('base', user.walletAddress)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 text-sm hover:underline font-mono"
              >
                {user.walletAddress.slice(0, 10)}...{user.walletAddress.slice(-8)}
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/40 uppercase tracking-wider mb-1">User ID</label>
            <code className="text-white/70 text-sm font-mono">{user?.id || '-'}</code>
          </div>
          <div>
            <label className="block text-xs text-white/40 uppercase tracking-wider mb-1">Agents</label>
            <p className="text-white text-sm">{agents.length} active agents</p>
          </div>
          {user?.email && (
            <div>
              <label className="block text-xs text-white/40 uppercase tracking-wider mb-1">Email</label>
              <p className="text-white/70 text-sm">{user.email}</p>
            </div>
          )}
        </div>
      </SettingsCard>

      {/* API Keys Section */}
      <SettingsCard title="API Keys" icon={<Icons.ApiKey size={18} />}>
        <p className="text-white/50 text-sm mb-4">
          API keys are generated when you create an agent. Each agent has its own unique API key for authentication.
        </p>
        
        {agents.length === 0 ? (
          <div className="py-8 text-center">
            <div className="w-12 h-12 rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
              <Icons.ApiKey size={20} className="text-white/30" />
            </div>
            <p className="text-white/40 text-sm">Create an agent to generate an API key</p>
          </div>
        ) : (
          <div className="space-y-3">
            {agents.map((agent) => (
              <AgentKeyRow key={agent.id} agent={agent} />
            ))}
          </div>
        )}

        <div className="mt-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
          <p className="text-yellow-400/80 text-sm">
            API keys are shown only once when an agent is created. If you've lost your key, you'll need to regenerate it (coming soon).
          </p>
        </div>
      </SettingsCard>

      {/* Connected Wallet Section */}
      <SettingsCard title="Connected Wallet" icon={<Icons.Wallets size={18} />}>
        {user?.walletAddress ? (
          <div className="p-4 rounded-xl bg-black/50 border border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0052FF]/20 flex items-center justify-center">
                  <div className="w-5 h-5 rounded-full border-2 border-[#0052FF]" />
                </div>
                <div>
                  <p className="text-white font-medium">Robinhood Wallet</p>
                  <code className="text-white/50 text-xs font-mono">{user.walletAddress}</code>
                </div>
              </div>
              <a
                href={getExplorerAddressUrl('base', user.walletAddress)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg text-white/40 hover:text-white/70 hover:bg-white/5 transition-all"
              >
                <Icons.External size={16} />
              </a>
            </div>
          </div>
        ) : (
          <p className="text-white/40 text-sm">No wallet connected</p>
        )}
      </SettingsCard>

      {/* Documentation */}
      <SettingsCard title="Resources" icon={<Icons.Docs size={18} />}>
        <div className="space-y-2">
          <a 
            href="https://docs.aibl.money" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.10] transition-all group"
          >
            <div className="flex items-center gap-3">
              <Icons.Docs size={18} className="text-white/50" />
              <span className="text-white">Documentation</span>
            </div>
            <Icons.External size={16} className="text-white/30 group-hover:text-white/50" />
          </a>
          <a 
            href="https://api.aibl.dev" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.10] transition-all group"
          >
            <div className="flex items-center gap-3">
              <Icons.ApiKey size={18} className="text-white/50" />
              <span className="text-white">API Reference</span>
            </div>
            <Icons.External size={16} className="text-white/30 group-hover:text-white/50" />
          </a>
        </div>
      </SettingsCard>
    </div>
  );
}

function SettingsCard({ title, icon, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-white/[0.02] border border-white/[0.06]"
    >
      <div className="px-6 py-4 border-b border-white/[0.06] flex items-center gap-3">
        <span className="text-white/50">{icon}</span>
        <h3 className="text-white font-medium">{title}</h3>
      </div>
      <div className="p-6">
        {children}
      </div>
    </motion.div>
  );
}

function AgentKeyRow({ agent }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(agent.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/[0.04]">
      <div>
        <p className="text-white text-sm font-medium">{agent.name}</p>
        <code className="text-white/40 text-xs font-mono">{agent.id}</code>
      </div>
      <div className="flex items-center gap-2">
        <span className="px-2 py-1 rounded text-[10px] font-medium text-white/40 bg-white/[0.03]">
          Key Hidden
        </span>
        <button
          onClick={handleCopy}
          className="p-2 rounded-lg text-white/40 hover:text-white/70 hover:bg-white/5 transition-all"
          title="Copy Agent ID"
        >
          {copied ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-400">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          ) : (
            <Icons.Copy size={14} />
          )}
        </button>
      </div>
    </div>
  );
}
